import { BehaviorSubject, switchMap } from 'rxjs';
import {
  ClaimRequirement,
  ClaimRequirementStatus,
  ClaimWithCase,
} from './claims.service';
import {
  Message,
  MessageService,
  ReadEntry,
} from './communication_message.service';

import { Injectable } from '@angular/core';
import { TaskComment, TaskPriority } from 'src/app/modules/firm/pages/agent-dashboard/models/task.model';
import { FilterChange } from 'src/app/modules/firm/pages/messages/messages-filters/messages-filters.component';
import { Base } from '../models/base.model';
import { DatabaseService } from './base/database.service';
import { UserService } from './user.service';

@Injectable({
  providedIn: 'root',
})
export class CommunicationService extends DatabaseService<Thread> {
  notifier: BehaviorSubject<boolean> = new BehaviorSubject(true);
  private readonly cpTaskSelectFields =
    'id, data, client_id, title, description, status_id, priority, assignee_id, due_date, category_id, subcategory_id, tags, comments, type, category_join:cp_task_category!cp_task_category_id_fkey(label),subcategory_join:cp_task_category!cp_task_subcategory_id_fkey(label)';

  constructor(private us: UserService, private ms: MessageService) {
    super('cp_thread');
    this.lambdaNames = {
      create: 'create_thread',
      update: 'update_thread',
      delete: 'delete_thread',
    };
  }

  notifyChanged() {
    this.notifier.next(true);
  }

  listCommunicationsWithMessagesForUser(fromUserId: string) {
    return this.notifier.pipe(
      switchMap(async (e) => {
        const query = this.supabase
          .from('cp_thread')
          .select('*, cp_message (*)')
          .eq('started_by_user_id', fromUserId);
        const { data, error } = await query;

        if (error) throw new Error(error.message);

        const procData = data as unknown as ThreadWithMessages[];
        // console.log('COMM SERVICE', data);

        return procData.filter((t) => checkIfIsUnread(t));
      })
    );
  }

  async getCommunicationsWithMessagesForThread(id: string) {
    const query = this.supabase
      .from('cp_thread')
      .select('*, cp_message (*)')
      .eq('id', id);
    const { data, error } = await query;

    if (error) throw new Error(error.message);

    console.log('COMM SERVICE', data);

    return data[0] as unknown as ThreadWithMessages;
  }

  async listAllCommunicationsWithLatestMessage(
    config: {
      page: number;
      limit: number;
      filters: FilterChange;
    } = {
        page: 1,
        limit: 10,
        filters: {
          read: 'all',
          sender: 'all',
          flag: 'all',
          text: '',
          claimId: null,
          allForClaim: false,
        },
      }
  ) {
    // cpv_threads_with_latest_messages has no PostgREST grant anymore: it ran with
    // security_invoker off and exposed every thread to any authenticated user.
    // security_invoker = on statement-timeouts (the non-admin cp_thread policy goes
    // through private.cpv_rls_thread), so threads_list serves it as service_role and
    // applies the AGENT scope in SQL.
    const res = await this.callLambda('threads_list', 'POST', {
      page: config.page,
      limit: config.limit,
      filters: config.filters,
    });

    const body: any = res?.details ?? res ?? {};
    return (body.data ?? []) as ThreadWithMessages[];
  }

  async listConversationByThreadId(
    id: string,
    config: {
      page: number;
      limit: number;
    } = {
        page: 1,
        limit: 10,
      }
  ) {
    const start = (config.page - 1) * config.limit;
    const end = start + config.limit - 1;

    const query = this.supabase
      .from('cp_thread')
      .select(
        `*, cp_message(*), cp_claim(id, cp_claim_permission(id, principal_id, cp_user(display_name, avatar_url, cp_pii(*)))), cp_user!started_by_user_id(display_name, avatar_url)`
      )
      .eq('id', id)
      .order('created_at', { foreignTable: 'cp_message', ascending: false })
      .range(start, end, { foreignTable: 'cp_message' })
      .single();

    const { data, error } = await query;
    if (error) throw new Error(error.message);

    return data as unknown as ThreadWithMessages;
  }

  async listCommunicationsWithMessagesForClaim(claimId: string) {
    const query = this.supabase
      .from('cp_thread')
      .select('*, cp_message (*))')
      .eq('related_claim_id', claimId);
    const { data, error } = await query;

    console.log(
      'Comm Service -> listCommunicationsWithMessagesForClaim',
      claimId,
      data
    );

    if (error) throw new Error(error.message);

    return data as unknown as ThreadWithMessages[];
  }
  async getCommunicationsWithMessagesAndCaseForThread(
    id: string
  ): Promise<ThreadWithMessagesAndClaimCase> {
    try {
      const query = this.supabase
        .from('cp_thread')
        .select(
          `
          *,
          cp_message (*, cp_user!cp_message_author_user_id_fkey(*)),
          cp_claim (*, cp_case(*))
        `
        )
        .eq('id', id);

      const { data, error } = await query;

      if (error) {
        throw new Error(error.message);
      }

      console.log('COMM SERVICE', data);

      this.addReadToThread(id);
      return data[0] as unknown as ThreadWithMessagesAndClaimCase;
    } catch (error) {
      console.error(
        'Error fetching communications with messages and case for thread:',
        error
      );
      throw error;
    }
  }

  async listCommunicationsWithMessagesAndCaseForClaim(
    claimId: string,
    pagination?: { page: number; limit: number }
  ) {
    let query = this.supabase
      .from('cp_thread')
      .select('*, cp_message (*), cp_claim(*, cp_case(*))', {
        count: 'estimated',
      })
      .eq('related_claim_id', claimId);

    if (pagination) {
      console.log('Pagination', pagination);
      query = query.range(
        pagination.page * pagination.limit,
        (pagination.page + 1) * pagination.limit - 1
      );
    }

    const response = await query;
    const data = response.data as unknown;
    const count = response.count;
    const error = response.error;

    console.log(
      'Comm Service -> listCommunicationsWithMessagesForClaim',
      claimId,
      data,
      count
    );

    if (error) throw new Error(error.message);

    const rows = (data as unknown as ThreadWithMessagesAndClaimCase[]) ?? [];
    await this.attachCpTasksToThreads(rows);
    this.normalizeThreadsEmbeddedCpTasks(rows);
    return { data: rows, count };
  }
  async listCommunicationsWithMessagesAndCaseForClaimList(claims: string[]) {
    const query = this.supabase
      .from('cp_thread')
      .select('*, cp_message (*), cp_claim(*, cp_case(*))')
      .in('related_claim_id', claims);
    const { data, error } = await query;

    console.log(
      'Comm Service -> listCommunicationsWithMessagesForClaim',
      claims,
      data
    );

    if (error) throw new Error(error.message);

    return data as unknown as ThreadWithMessagesAndClaimCase[];
  }

  async userRelatedCommunications(
    claims: string[],
    userID: string,
    pagination: { page: number; limit: number } = {
      page: 0,
      limit: 10,
    }
  ) {
    console.log('Comm Service -> userRelatedCommunications', claims, userID);

    const query = this.supabase
      .from('cp_thread')
      .select('*, cp_message (*), cp_claim(*, cp_case(*))', {
        count: 'estimated',
      })
      .in('related_claim_id', claims)
      .range(
        pagination.page * pagination.limit,
        (pagination.page + 1) * pagination.limit - 1
      );
    // .eq('started_by_user_id', userID);
    // .eq(`cp_message.author_user_id`, userID);

    // .or(
    //   `started_by_user_id.eq.${userID},cp_message.author_user_id.eq.${userID}`
    // );

    const { data, count, error } = await query;
    if (error) throw new Error(error.message);

    return { data: data as unknown as ThreadWithMessagesAndClaimCase[], count };
  }

  CWMToRequirements(cm: ThreadWithMessages[]): ClaimRequirement[] {
    if (!cm || cm.length == 0) {
      return [];
    }
    const thereq: ClaimRequirement = {
      claimId: cm[0].related_claim_id!,
      status: ClaimRequirementStatus.UNMET,
      requirement: {
        description: 'Unread message',
        chaseCycle: [],
        completionCriteria: 'Read message',
        config: {
          count: 0,
        },
        deadline: new Date(),
        headline: 'Unread message',
        lastUpdated: new Date(cm[0].created_at!),
        priority: 'MEDIUM',
        status: 'ACTIVE',
        type: 'MESSAGE',
      },
    };

    console.log('Comm service -> CWMToRequirements', cm);

    for (let i = 0; i < cm.length; i++) {
      const c = cm[i];

      const ur = c.cp_message!.reduce((prev, curr) => {
        let val = 0;
        if (curr.user_reads && curr.user_reads.length) {
          val = curr.user_reads.length;
        }
        return val + prev;
      }, 0);

      if (ur > 0) {
        thereq.requirement.config.count++;
      }
    }

    if (thereq.requirement.config.count > 0) {
      return [thereq];
    } else {
      return [];
    }
  }

  async closeThread(id: string) {
    const payload = { id };
    // @ts-ignore
    const res = await (this as any).callLambda(
      'close_thread',
      'PUT',
      payload,
      id
    );
    return res;
  }

  async sendMessage(m: Partial<Message>) {
    const ur: ReadEntry = {
      device: this.us.getFingerprint(),
      timestamp: new Date(),
      userId: (await this.us.getCurrentUser())?.id!,
    };
    m.user_reads = [ur];

    console.log('params to send message inside communication service: ', m)

    // @ts-ignore
    const res = await (this as any).callLambda('create_message', 'POST', m);
    return res;
  }

  async addReadToThread(thread_id: string) {
    const user = (await this.us.getCurrentUser())!;
    const payload = { thread_id, user_id: user.id };
    // @ts-ignore
    const res = await (this as any).callLambda(
      'mark_thread_read',
      'POST',
      payload
    );
    // notify listeners if server confirmed update
    this.notifyChanged();
    return res;
  }

  /**
   * Send SMS message via external API
   */
  async sendSMS(request: {
    medium: 'SMS';
    from?: { user_id?: string };
    to: { claim_id: string };
    content: { message: string };
  }): Promise<any> {
    const url = 'https://n8n.dev.somosclientportal.com/webhook/comms/channel/sms/send';

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(request)
    });

    console.log('SMS API response status:', response.status, response.statusText);

    if (!response.ok) {
      throw new Error(`SMS API call failed: ${response.status} ${response.statusText}`);
    }

    // Check if response has content and is JSON
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      try {
        return await response.json();
      } catch (jsonError) {
        console.warn('Failed to parse JSON response, but API call was successful:', jsonError);
        return { success: true, status: response.status };
      }
    } else {
      // If not JSON, try to get text content or return success indicator
      try {
        const textResponse = await response.text();
        console.log('SMS API text response:', textResponse);
        return { success: true, status: response.status, message: textResponse };
      } catch (textError) {
        console.warn('Failed to parse response as text, but API call was successful:', textError);
        return { success: true, status: response.status };
      }
    }
  }
  /**
   * Send Email message via external API (N8N)
   */
  // async sendEmail(request: {
  //   medium: 'EMAIL';
  //   from?: { user_id?: string };
  //   to: {
  //     claim_id: string;
  //     cc?: string;
  //     bcc?: string;
  //     replyto?: string;
  //   };
  //   content: {
  //     subject: string;
  //     message: string;
  //     html?: string;
  //     attachments?: any[];
  //   };
  // }): Promise<any> {
  //   const url = 'https://n8n.dev.somosclientportal.com/webhook/comms/channel/email/send';

  //   const response = await fetch(url, {
  //     method: 'POST',
  //     headers: {
  //       'Content-Type': 'application/json'
  //     },
  //     body: JSON.stringify(request)
  //   });

  //   console.log('Email API response status:', response.status, response.statusText);

  //   if (!response.ok) {
  //     throw new Error(`Email API call failed: ${response.status} ${response.statusText}`);
  //   }

  //   // Check if response has content and is JSON
  //   const contentType = response.headers.get('content-type');
  //   if (contentType && contentType.includes('application/json')) {
  //     try {
  //       return await response.json();
  //     } catch (jsonError) {
  //       console.warn('Failed to parse JSON response, but API call was successful:', jsonError);
  //       return { success: true, status: response.status };
  //     }
  //   } else {
  //     // If not JSON, try to get text content or return success indicator
  //     try {
  //       const textResponse = await response.text();
  //       console.log('Email API text response:', textResponse);
  //       return { success: true, status: response.status, message: textResponse };
  //     } catch (textError) {
  //       console.warn('Failed to parse response as text, but API call was successful:', textError);
  //       return { success: true, status: response.status };
  //     }
  //   }
  // }

  async sendEmailMessageOnLambda(
    request: {
      medium: 'EMAIL';
      from?: { user_id?: string };
      sender_email?: string;
      to: {
        claim_id: string;
        cc?: string;
        bcc?: string;
        replyto?: string;
      };
      content: {
        subject: string;
        message: string;
        html?: string;
        attachments?: any[];
      };
    }
  ): Promise<any> {

    console.log('recebendo inputs para o form: ', request);

    const params: any = {
      recipient_claim_id: request.to.claim_id,
      subject: request.content.subject,
      html_body: request.content.html,
      text_body: request.content.message,
      bcc: request.to.bcc ? request.to.bcc : '',
    };

    // Adicionar sender_email se fornecido, caso contrário usar sender_user_id
    if (request.sender_email) {
      params.sender_email = request.sender_email;
      console.log('📧 Enviando com sender_email:', request.sender_email);
    } else if (request.from?.user_id) {
      params.sender_user_id = request.from.user_id;
      console.log('👤 Enviando com sender_user_id:', request.from.user_id);
    }

    console.log('params to send message inside communication service: ', params)
    try {
      // @ts-ignore
      const res = await (this as any).callLambda('send_email', 'POST', params);
      return res;
    } catch (error: any) {
      console.error('❌ Erro ao enviar email via Lambda:', error);
      throw new Error('Failed to send email');
    }
  }

  /**
   * Send WhatsApp message via external API (currently disabled)
   */
  async sendWhatsApp(request: {
    medium: 'WHATSAPP';
    from?: { user_id?: string };
    to: { claim_id: string };
    content: { message: string };
  }): Promise<any> {
    const url = 'https://n8n.dev.somosclientportal.com/webhook/comms/channel/sms/send';

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(request)
    });

    console.log('WhatsApp API response status:', response.status, response.statusText);

    if (!response.ok) {
      throw new Error(`WhatsApp API call failed: ${response.status} ${response.statusText}`);
    }

    // Check if response has content and is JSON
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      try {
        return await response.json();
      } catch (jsonError) {
        console.warn('Failed to parse JSON response, but API call was successful:', jsonError);
        return { success: true, status: response.status };
      }
    } else {
      // If not JSON, try to get text content or return success indicator
      try {
        const textResponse = await response.text();
        console.log('WhatsApp API text response:', textResponse);
        return { success: true, status: response.status, message: textResponse };
      } catch (textError) {
        console.warn('Failed to parse response as text, but API call was successful:', textError);
        return { success: true, status: response.status };
      }
    }
  }

  private normalizeThreadsEmbeddedCpTasks(threads: ThreadWithMessagesAndClaimCase[]): void {
    for (const thread of threads) {
      const tasks = thread.cp_task;
      if (!Array.isArray(tasks) || !tasks.length) {
        continue;
      }
      thread.cp_task = tasks.map((row) => this.mapEmbeddedCpTaskToSummary(row));
    }
  }

  private extractThreadIdFromTaskData(data: unknown): string {
    if (!data || typeof data !== 'object' || Array.isArray(data)) {
      return '';
    }
    const threadId = (data as { thread_id?: unknown }).thread_id;
    return typeof threadId === 'string' ? threadId.trim() : '';
  }

  private buildThreadIdOrFilter(threadIds: string[]): string {
    return threadIds
      .map((id) => id.trim())
      .filter(Boolean)
      .map((id) => `data->>thread_id.eq.${id.replace(/,/g, '\\,')}`)
      .join(',');
  }

  private async attachCpTasksToThreads(threads: ThreadWithMessagesAndClaimCase[]): Promise<void> {
    for (const thread of threads) {
      thread.cp_task = [];
    }

    const threadIds = [...new Set(threads.map((thread) => thread.id?.trim() ?? '').filter(Boolean))];
    if (!threadIds.length) {
      return;
    }

    const filter = this.buildThreadIdOrFilter(threadIds);
    if (!filter) {
      return;
    }

    const { data, error } = await this.supabase
      .from('cp_task')
      .select(this.cpTaskSelectFields)
      .or(filter);

    if (error) {
      throw new Error(error.message);
    }

    const taskRows = (data ?? []) as Record<string, unknown>[];
    const tasksByThreadId = new Map<string, Record<string, unknown>[]>();

    for (const row of taskRows) {
      const threadIdFromData = this.extractThreadIdFromTaskData(row['data']);
      if (!threadIdFromData) {
        continue;
      }
      const bucket = tasksByThreadId.get(threadIdFromData) ?? [];
      bucket.push(row);
      tasksByThreadId.set(threadIdFromData, bucket);
    }

    for (const thread of threads) {
      const threadId = thread.id?.trim() ?? '';
      thread.cp_task = (tasksByThreadId.get(threadId) ?? []) as unknown as ThreadTaskSummary[];
    }
  }

  /**
   * Maps a raw cp_task-joined row (as returned from Supabase, possibly with joined category)
   * into a normalized ThreadTaskSummary object. This acts as a defensive adapter for uncertain
   * db join shapes, guaranteeing our app gets consistently typed task summary objects.
   *
   * Handles the following:
   *  - Category label: Accepts either object or array form from left join, safely extracts `label`.
   *  - Defensive string coercion on id, title, and other string fields.
   *  - Accepts both thread_id column or resolves from cp_task.data if missing.
   *  - Returns `undefined` for missing or non-string fields (e.g. assignee_name).
   *  - Passes through tags/comments arrays only if valid.
   *  - Always falls back to priority 'Medium' and validates type.
   *
   * This is a backend-protection function: we can't trust Supabase join/row shapes,
   * so this prevents crashes in Kanban and threads code.
   */
  private mapEmbeddedCpTaskToSummary(raw: unknown): ThreadTaskSummary {
    const r = raw as Record<string, unknown>;
    const labelFrom = (nested: unknown): string => {
      if (nested && typeof nested === 'object' && !Array.isArray(nested) && typeof (nested as { label?: unknown }).label === 'string') {
        return String((nested as { label: string }).label).trim();
      }
      if (Array.isArray(nested) && nested[0] && typeof (nested[0] as { label?: unknown }).label === 'string') {
        return String((nested[0] as { label: string }).label).trim();
      }
      return '';
    };
    const categoryJoin = r['category_join'] ?? r['cp_task_category'];
    const label = labelFrom(categoryJoin);
    const subNested = r['subcategory_join'];
    const subLabel = labelFrom(subNested);
    const categoryId =
      typeof r['category_id'] === 'string' && r['category_id'].trim() ? String(r['category_id']).trim() : '';
    const subcategoryId =
      typeof r['subcategory_id'] === 'string' && r['subcategory_id'].trim() ? String(r['subcategory_id']).trim() : '';
    const title = typeof r['title'] === 'string' ? String(r['title']).trim() : '';
    const description = typeof r['description'] === 'string' ? String(r['description']) : '';
    const priority = (r['priority'] as TaskPriority | undefined) ?? 'Medium';
    const rawType = r['type'];
    const taskType =
      typeof rawType === 'string' || rawType === null ? (rawType as string | null) : undefined;
    const threadIdFromData = this.extractThreadIdFromTaskData(r['data']);
    const rawData = r['data'];
    const data =
      rawData && typeof rawData === 'object' && !Array.isArray(rawData)
        ? (rawData as Record<string, unknown>)
        : undefined;
    return {
      id: String(r['id'] ?? ''),
      thread_id:
        typeof r['thread_id'] === 'string' && r['thread_id'].trim()
          ? r['thread_id'].trim()
          : threadIdFromData || undefined,
      data,
      title,
      description,
      status: typeof r['status_id'] === 'string' ? r['status_id'] : undefined,
      assignee_id: typeof r['assignee_id'] === 'string' ? r['assignee_id'] : undefined,
      assignee_name: typeof r['assignee_name'] === 'string' ? r['assignee_name'] : undefined,
      assignee_initials: typeof r['assignee_initials'] === 'string' ? r['assignee_initials'] : undefined,
      due_date: typeof r['due_date'] === 'string' ? r['due_date'] : undefined,
      category_id: categoryId,
      category: label,
      subcategory_id: subcategoryId,
      subcategory: subLabel,
      tags: Array.isArray(r['tags']) ? (r['tags'] as string[]) : undefined,
      comments: Array.isArray(r['comments']) ? (r['comments'] as TaskComment[]) : undefined,
      priority,
      type: taskType,
    };
  }
}

class Thread extends Base {
  subject?: string;
  medium?: 'CHAT' | 'INTERNAL_MESSAGE' | 'EMAIL';
  related_claim_id?: string;
  started_by_user_id?: string;
  open_for_replies?: boolean;
  attributes?: any;
  language?: string;
  started_at?: Date;
}

export interface SendEmailRequest {
  medium: 'EMAIL';
  from?: {
    user_id?: string;
  };
  to: {
    claim_id: string;
    cc?: string;
    bcc?: string;
    replyto?: string;
  };
  content: {
    subject: string;
    message: string;
    html?: string;
    attachments?: any[];
  };
}

export class ThreadWithMessages extends Thread {
  cp_message?: Message[];
  last_message_created_at?: Date;
  cp_task?: ThreadTaskSummary[];
}
export class ThreadWithMessagesAndClaimCase extends Thread {
  cp_message?: Message[];
  cp_claim?: ClaimWithCase;
  cp_task?: ThreadTaskSummary[];
}

export interface ThreadTaskSummary {
  id: string;
  thread_id?: string;
  data?: Record<string, unknown> | null;
  title?: string | null;
  description?: string | null;
  status?: string;
  assignee_id?: string;
  assignee_name?: string;
  assignee_initials?: string;
  due_date?: string;
  category_id?: string;
  category: string;
  subcategory_id?: string;
  subcategory?: string;
  tags?: string[];
  comments?: TaskComment[];
  priority: TaskPriority;
  type?: string | null;
}

export function checkIfIsUnread(t: ThreadWithMessagesAndClaimCase): boolean {
  // console.log('Checking if unread', t);

  if (!t.cp_message) {
    return true;
  }

  // console.log('Checking if unread > has messages', t.cp_message);

  return (
    t.cp_message!.filter((m) => !m.user_reads || !m.user_reads.length).length >
    0
  );
}
